import { BOX_TRUCK_EQUIPMENT_LABELS, migrateBoxTruckEquipmentLabel } from '@numeo/types-core'

/** The only part of a selected driver this needs: what the operator recorded them as driving. */
interface DriverWithEquipment {
    boxTruckInfo: { info?: { equipmentType?: string | null } | null }
}

/** The box-truck labels a set of drivers actually run, migrated off any legacy spelling and deduped. */
export const driverEquipmentLabels = (drivers: readonly DriverWithEquipment[]): string[] => [
    ...new Set(
        drivers
            .map((driver) => driver.boxTruckInfo.info?.equipmentType)
            .filter((equipment): equipment is string => !!equipment)
            .map(migrateBoxTruckEquipmentLabel)
            .filter((equipment) => BOX_TRUCK_EQUIPMENT_LABELS.includes(equipment))
    ),
]

/**
 * The equipment filter after picking drivers: what the operator chose, PLUS what those drivers drive.
 *
 * Picking drivers used to REPLACE the selection, so an operator who had ticked Cargo Van and Sprinter and then
 * picked a few cargo-van drivers watched Sprinter switch itself off — the board quietly stopped searching for
 * half of what they had asked for, and nothing on screen said why.
 *
 * A union instead: the drivers' classes are added because a truck you just pointed at should be searched for,
 * and the operator's own ticks stay because they are the one thing here that was chosen deliberately. It
 * follows that a pick whose drivers have no recorded equipment leaves the filter alone rather than clearing it
 * — clearing would not be "no match", it would be NO equipment filter at all, widening the search to every
 * class.
 *
 * Ordered by the catalogue, not by when each label arrived, so the same set always serialises identically
 * (the tab handoff and the filter summary both compare these arrays).
 */
export const withDriverEquipment = (picked: readonly string[], drivers: readonly DriverWithEquipment[]): string[] => {
    const merged = new Set([...picked, ...driverEquipmentLabels(drivers)])
    return BOX_TRUCK_EQUIPMENT_LABELS.filter((label) => merged.has(label))
}
